import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { JumpToComponent } from '../../shared/components/jump-to/jump-to.component';
import { ScrollSpyService } from '../../shared/services/scroll-spy.service';
import { Observable } from 'rxjs';
import { appPaths } from '../../app.paths';
import { RealVoices } from './real-voices/real-voices';
import { HowToExplain } from './how-to-explain/how-to-explain';
import { CtaBottomComponent } from '../../shared/components/cta-bottom/cta-bottom.component';
import { HcpResources } from './hcp-resources/hcp-resources';
import { PatientResources } from './patient-resources/patient-resources';
import { References } from './references/references';
import { Information } from './information/information';
import { ReviewBarComponent } from '../../shared/components/review-bar/review-bar.component';
import {
  ResourceHeroComponent,
  ResourceHeroSlide,
} from '../../shared/components/resource-hero/resource-hero.component';
import { AditionalResourcesComponent } from './aditional-resources/aditional-resources.component';

@Component({
  selector: 'app-resource',
  imports: [
    JumpToComponent,
    RealVoices,
    HowToExplain,
    HcpResources,
    PatientResources,
    CtaBottomComponent,
    References,
    Information,
    ResourceHeroComponent,
    ReviewBarComponent,
    AditionalResourcesComponent
  ],
  templateUrl: './resource.html',
  styleUrl: './resource.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Resource {
  scrollSpyService = inject(ScrollSpyService);
  activeSection$!: Observable<string | null>;

  imageTitle = {
    containerSize: '1162',
    color: 'yellow',
    image: 'home/resources',
    alt: 'Cassie Bailey and her son outdoors in the woods',
    title: `Haven't been screened yet?`,
    text: [
      `Then now is the time. Visit our resources page or chat with a counselor for live support and tools that help every step of the way.`,
    ],
    cta: {
      title: 'Get resources',
      url: appPaths.USEFULTOOLS + '/',
      openInNewTab: false,
    },
  };

  heroSlides: ResourceHeroSlide[] = [
    {
      id: 'hcp-resources',
      backgroundImageName: 'crosby-bg',
      backgroundUsesTabletVariant: false,
      sillhouetteImageName: 'crosby-head',
      sillhouetteUsesTabletVariant: false,
      sillhouette: {
        mobile: {
          width: 135,
          height: 57,
          top: -20,
          right: 116,
        },
        tablet: {
          width: 149,
          height: 41,
          top: -4,
          right: 181,
        },
        desktop: {
          width: 210,
          height: 58,
          top: -6,
          right: 256,
        },
      },
      disclaimer: {
        mobile: {
          left: 64,
          bottom: 11,
        },
        tablet: {
          left: 64,
          bottom: 105,
        },
        desktop: {
          left: 8,
          bottom: 34,
        },
      },
      sillhouetteAlt: 'Dr. Crosby posing with her arm resting on a table and smiling',
      title: `INFORMING <br class='d-none d-lg-block'>PATIENTS ABOUT <span class="text-primary"><em>SCREENING</em></span>`,
      text: `From patient stories and common FAQs to insurance billing codes, we have the resources to help you educate and inform your patients.`,
      isFirstSlide: true
    },
  ];

  jumpToData = [
    {
      buttonLabel: 'How to screen',
      buttonUrl: '/resource',
      buttonFragment: 'how-to-screen',
    },
    {
      buttonLabel: 'Real voices',
      buttonUrl: '/resource',
      buttonFragment: 'real-voices',
    },
    {
      buttonLabel: 'FAQs',
      buttonUrl: '/resource',
      buttonFragment: 'faqs',
    },
    {
      buttonLabel: 'HCP resources',
      buttonUrl: '/resource',
      buttonFragment: 'hcp-resources',
    },
    {
      buttonLabel: 'Patient resources',
      buttonUrl: '/resource',
      buttonFragment: 'patient-resources',
    },
  ];

  ctaBottomData = {
    icon: 'mail',
    alt: 'An envelope',
    title: `Get updates and more information<br class='d-none d-lg-block'> about screening <span class="d-inline-block">and management.</span>`,
    button: {
      buttonLabel: 'Sign up to stay connected',
      buttonUrl: '/get-updates/',
      openInNewTab: false,
      buttonIcon: 'right-icon',
    },
  };

  ngOnInit(): void {
    const sectionIds = this.jumpToData.map((item) => item.buttonFragment);
    this.scrollSpyService.initObserver(sectionIds);

    this.activeSection$ = this.scrollSpyService.activeSection$;
  }

  ngOnDestroy(): void {
    this.scrollSpyService.disconnectObserver();
  }
}
